import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useSubscription } from '../../hooks/useSubscription'; // Add subscription hook
import { FaArrowLeft, FaExclamationTriangle, FaSpinner, FaCheckCircle, FaBook, FaVideo, FaPencilAlt, FaCalendarAlt } from 'react-icons/fa';
import InternalNavbar from '../../components/InternalNavbar';
import Footer from '../../components/footer';
import Scrollbar from '../../components/scrollbar';
import courseService from '../../services/courseService';
import arabicBg from '../../images/arabic-bg-dark.svg';
import {
  formatDate,
  getLevelImage,
  isUUID,
  handleImageError,
  getImageSrcSet,
  getImageSizes,
  parseApiError,
  safeNumber
} from './helpers';
import './style.css';

const LevelDetailsPage = () => {
  // State management
  const [level, setLevel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  // Subscription context
  const { hasActiveSubscription, isDemoSubscription, isPaidSubscription } = useSubscription();

  // Routing
  const { levelParam } = useParams();
  const navigate = useNavigate();

  // Fetch level data on component mount
  useEffect(() => {
    const fetchLevelData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Detect if parameter is UUID or level code using helper function
        const isUUIDParam = isUUID(levelParam);

        let levelData;
        if (isUUIDParam) {
          // Fetch by ID
          levelData = await courseService.getCourseById(levelParam);
        } else {
          // Fetch by level code (A0, A1, A2, A3, B1, B2, B3)
          levelData = await courseService.getCourseByLevel(levelParam);
        }

        setLevel(levelData);
      } catch (err) {
        console.error('Error fetching level data:', err);
        
        // Use helper function to parse error
        const errorInfo = parseApiError(err);
        setError(errorInfo.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLevelData();
  }, [levelParam]);

  // Toggle features visibility
  const toggleFeatures = () => {
    setShowAllFeatures(!showAllFeatures);
  };

  // Retry function for error state
  const handleRetry = () => {
    window.location.reload();
  };

  // Loading state
  if (loading) {
    return (
      <div className="level-details-page">
        <InternalNavbar />
        <div className="level-details-container">
          <div className="loading-state" role="status" aria-live="polite">
            <div className="loading-content">
              <FaSpinner className="loading-spinner" aria-hidden="true" />
              <h2>Loading Level Details...</h2>
              <p>Please wait while we fetch the information.</p>
            </div>
          </div>
        </div>
        <Footer />
        <Scrollbar />
      </div>
    );
  }

  // Error state - 404
  if (error === 'Level not found') {
    return (
      <div className="level-details-page">
        <InternalNavbar />
        <div className="level-details-container">
          <div className="not-found-state" role="alert">
            <div className="not-found-content">
              <h1>Level Not Found</h1>
              <p>The level you're looking for doesn't exist.</p>
              <Link to="/levels" className="back-btn" aria-label="Return to levels list page">
                Back to Levels
              </Link>
            </div>
          </div>
        </div>
        <Footer />
        <Scrollbar />
      </div>
    );
  }

  // Error state - General error
  if (error) {
    return (
      <div className="level-details-page">
        <InternalNavbar />
        <div className="level-details-container">
          <div className="error-state" role="alert">
            <div className="error-content">
              <FaExclamationTriangle className="error-icon" aria-hidden="true" />
              <h1>Unable to Load Level</h1>
              <p>{error}</p>
              <button onClick={handleRetry} className="retry-btn" aria-label="Retry loading level details">
                Try Again
              </button>
            </div>
          </div>
        </div>
        <Footer />
        <Scrollbar />
      </div>
    );
  }

  // Main content - level data loaded successfully
  return (
    <div className="level-details-page">
      <InternalNavbar />

      {/* Arabic Background */}
      <div 
        className="arabic-background"
        style={{
          backgroundImage: `url(${arabicBg})`,
        }}
        aria-hidden="true"
      />

      <div className="level-details-container">
        <div className="level-details-wrapper">
          {/* Back Navigation */}
          <nav className="back-navigation" aria-label="Breadcrumb">
            <Link to="/levels" className="back-link" aria-label="Return to levels list page">
              <FaArrowLeft aria-hidden="true" /> Back to Levels
            </Link>
          </nav>

          {/* Hero Section */}
          <header className="level-hero">
            <div className="level-hero-content">
              <div className="level-hero-image-container">
                <img 
                  src={getLevelImage(level)} 
                  srcSet={getImageSrcSet(getLevelImage(level))}
                  sizes={getImageSizes()}
                  alt={`${level.title} course image`}
                  className="level-hero-image"
                  loading="lazy"
                  decoding="async"
                  onError={handleImageError}
                  width="300"
                  height="300"
                />
                <div className="level-badge" aria-label={`Level ${level.level}`}>
                  {level.level}
                </div>
              </div>
              <div className="level-hero-text">
                <h1 className="level-details-title">{level.title}</h1>
              </div>
            </div>
          </header>

          {/* Description Section */}
          <section className="level-details-description" aria-labelledby="level-description-heading">
            <h2 id="level-description-heading" className="visually-hidden">Course Description</h2>
            <p>{level.description}</p>
          </section>

          {/* Metrics Grid */}
          <section className="metrics-grid" aria-labelledby="metrics-heading">
            <h2 id="metrics-heading" className="visually-hidden">Course Statistics</h2>
            <div className="metric-card" role="group" aria-label={`${safeNumber(level.lessons_count)} lessons in this course`}>
              <FaBook className="metric-icon" aria-hidden="true" />
              <div className="metric-number" aria-label={`${safeNumber(level.lessons_count)} lessons`}>{safeNumber(level.lessons_count)}</div>
              <div className="metric-label">Lessons</div>
            </div>
            <div className="metric-card" role="group" aria-label={`${safeNumber(level.videos_count)} videos in this course`}>
              <FaVideo className="metric-icon" aria-hidden="true" />
              <div className="metric-number" aria-label={`${safeNumber(level.videos_count)} videos`}>{safeNumber(level.videos_count)}</div>
              <div className="metric-label">Videos</div>
            </div>
            <div className="metric-card" role="group" aria-label={`${safeNumber(level.exercises_count)} exercises in this course`}>
              <FaPencilAlt className="metric-icon" aria-hidden="true" />
              <div className="metric-number" aria-label={`${safeNumber(level.exercises_count)} exercises`}>{safeNumber(level.exercises_count)}</div>
              <div className="metric-label">Exercises</div>
            </div>
          </section>

          {/* Features Section */}
          {level.features && level.features.length > 0 && (
            <section className="features-section" aria-labelledby="features-heading">
              <h2 id="features-heading" className="features-title">Key Features</h2>
              <ul className="features-grid" role="list">
                {(showAllFeatures ? level.features : level.features.slice(0, 6)).map((feature, index) => (
                  <li key={index} className="feature-item">
                    <div className="feature-icon" aria-hidden="true">
                      <FaCheckCircle />
                    </div>
                    <div className="feature-text">{feature}</div>
                  </li>
                ))}
              </ul>
              {level.features.length > 6 && (
                <button 
                  onClick={toggleFeatures} 
                  className="show-more-btn"
                  aria-expanded={showAllFeatures}
                  aria-label={showAllFeatures ? 'Show fewer features' : `Show ${level.features.length - 6} more features`}
                >
                  {showAllFeatures ? 'Show Less' : 'Show More'}
                </button>
              )}
            </section>
          )}

          {/* Timestamps Section */}
          <section className="timestamps-section" aria-labelledby="timestamps-heading">
            <h2 id="timestamps-heading" className="visually-hidden">Course Dates</h2>
            <div className="timestamp-item">
              <FaCalendarAlt className="timestamp-icon" aria-hidden="true" />
              <span>Created: <time dateTime={level.created_at}>{formatDate(level.created_at)}</time></span>
            </div>
            {level.updated_at && level.updated_at !== level.created_at && (
              <div className="timestamp-item">
                <FaCalendarAlt className="timestamp-icon" aria-hidden="true" />
                <span>Updated: <time dateTime={level.updated_at}>{formatDate(level.updated_at)}</time></span>
              </div>
            )}
          </section>

          {/* Action Buttons */}
          <nav className="action-buttons" aria-label="Course actions">
            {/* Determine button text and action based on subscription status */}
            {(() => {
              if (!hasActiveSubscription) {
                // No subscription - redirect to demo activation
                return (
                  <button 
                    className="btn-start-learning"
                    onClick={() => navigate('/demo-confirmation')}
                    aria-label="Access demo"
                  >
                    Access Demo
                  </button>
                );
              } else if (isDemoSubscription) {
                // Demo subscription - show demo access
                return (
                  <button 
                    className="btn-start-learning"
                    onClick={() => navigate(`/levels/${level.level}/lessons`)}
                    aria-label={`Access demo for ${level.title}`}
                  >
                    Access Demo
                  </button>
                );
              } else if (isPaidSubscription) {
                // Premium subscription - show start learning
                return (
                  <button 
                    className="btn-start-learning"
                    onClick={() => navigate(`/levels/${level.level}/lessons`)}
                    aria-label={`Start learning ${level.title}`}
                  >
                    Start Learning
                  </button>
                );
              }
              // Default fallback
              return (
                <button 
                  className="btn-start-learning"
                  onClick={() => navigate(`/levels/${level.level}/lessons`)}
                  aria-label={`Start learning ${level.title}`}
                >
                  Start Learning
                </button>
              );
            })()}
            <Link to="/levels" className="btn-back" aria-label="Return to levels list page">
              Back to Levels
            </Link>
          </nav>
        </div>
      </div>

      <Footer />
      <Scrollbar />
    </div>
  );
};

export default LevelDetailsPage;
